import type { TimeEntry } from './types';

/**
 * Esri publishes ~200 Wayback releases, but at any one place almost all of them
 * carry the same picture — imagery is only re-flown where something changed. A
 * strip of 200 dates is therefore misleading: most of them do nothing.
 *
 * The tilemap endpoint says, for a given tile, which release actually supplies the
 * imagery (`select`). So walk the releases newest to oldest: ask which release
 * serves this tile, record that one, then jump straight to the release below it
 * and repeat. That yields exactly the versions that differ here, in about as many
 * requests as there are versions — typically a handful, not 200.
 */

const CONFIG_URL = 'https://s3-us-west-2.amazonaws.com/config.maptiles.arcgis.com/waybackconfig.json';
const TILEMAP = 'https://wayback.maptiles.arcgis.com/arcgis/rest/services/world_imagery/wmts/1.0.0/default028mm/MapServer/tilemap';

interface Release {
  num: number;
  date: string;
}

let releasesPromise: Promise<Release[]> | undefined;

/** All releases, newest first. */
function loadReleases(): Promise<Release[]> {
  if (!releasesPromise) {
    releasesPromise = fetch(CONFIG_URL)
      .then((r) => r.json())
      .then((cfg: Record<string, { itemTitle?: string }>) => {
        const out: Release[] = [];
        for (const [num, item] of Object.entries(cfg)) {
          const date = /(\d{4}-\d{2}-\d{2})/.exec(item.itemTitle ?? '')?.[1];
          if (date) out.push({ num: Number(num), date });
        }
        out.sort((a, b) => b.date.localeCompare(a.date));
        return out;
      })
      .catch(() => []);
  }
  return releasesPromise;
}

function tileCoord(center: [number, number], zoom: number): { z: number; row: number; col: number } {
  const z = Math.min(16, Math.max(10, Math.round(zoom)));
  const size = 20037508.342789244;
  const n = 2 ** z;
  const col = Math.floor(((center[0] + size) / (2 * size)) * n);
  const row = Math.floor(((size - center[1]) / (2 * size)) * n);
  return { z, row, col };
}

const cache = new Map<string, Promise<TimeEntry[]>>();

/** Releases whose imagery genuinely differs at this location, oldest first. */
export function localVersions(center: [number, number], zoom: number): Promise<TimeEntry[]> {
  const { z, row, col } = tileCoord(center, zoom);
  const key = `${z}/${row}/${col}`;
  let pending = cache.get(key);
  if (pending) return pending;

  pending = (async () => {
    const releases = await loadReleases();
    if (!releases.length) return [];

    const found: TimeEntry[] = [];
    const seen = new Set<number>();
    let index = 0;

    while (index < releases.length && found.length < 40) {
      const candidate = releases[index];
      let selected: number | undefined;
      try {
        const res = await fetch(`${TILEMAP}/${candidate.num}/${z}/${row}/${col}`);
        if (res.ok) {
          const json = (await res.json()) as { select?: number[]; valid?: boolean };
          if (json.valid !== false) selected = json.select?.[0];
        }
      } catch {
        /* network hiccup — fall through and step one release back */
      }

      if (selected === undefined) {
        index++;
        continue;
      }

      const at = releases.findIndex((r) => r.num === selected);
      const release = at >= 0 ? releases[at] : candidate;
      if (!seen.has(release.num)) {
        seen.add(release.num);
        found.push({ id: String(release.num), label: release.date });
      }
      // Everything newer than `release` shows this same image; continue below it.
      index = (at >= 0 ? at : index) + 1;
    }

    return found.reverse(); // oldest first, like every other series
  })();

  cache.set(key, pending);
  return pending;
}
